<template>
    <ion-page class="ion-page" id="main">
        <default-header/>

        <ion-content ref="content" fullscreen>
            <ion-refresher slot="fixed" @ionRefresh="refresh($event)">
                <ion-refresher-content></ion-refresher-content>
            </ion-refresher>

            <flash-messages/>

            <div v-if="incomingMailAddress">
                <ion-item>
                    <ion-text>
                        {{
                            $t('ncrypt.incoming_mail.your_incoming_mail_address_description').replace('{incoming_mail_address}', incomingMailAddress)
                        }}
                    </ion-text>
                </ion-item>

                <ion-item lines="none">
                    <ion-label>
                        {{ $t('ncrypt.incoming_mail.your_incoming_mail_address_label') }} {{ incomingMailAddress }}
                    </ion-label>
                </ion-item>

                <incoming-mail-messages
                    :messages="messages"
                    :pagination="$store.getters.incomingMailPagination"
                    :showSkeleton="showSkeleton"
                    :listTitle="$t('ncrypt.incoming_mail.title')"
                    type="inbox"
                    v-show="!initialized || initialized && hasMessages"
                    ref="messages"
                    v-on:refresh="refresh($event)"
                    v-on:load-data="loadData($event)"
                />

                <div v-show="!busy && !hasMessages">
                    <ion-card class="floating">
                        <ion-card-content>
                            <ion-label>{{ $t('ncrypt.incoming_mail.no_messages') }}</ion-label>
                        </ion-card-content>
                    </ion-card>
                </div>
            </div>
            <div
                v-else-if="$store.getters.user && $store.getters.user.company && $store.getters.user.company.incoming_mail_address">
                <ion-card class="floating">
                    <ion-card-content>
                        <ion-label>{{ $t('ncrypt.incoming_mail.user_start_using_incoming_mail_label') }}</ion-label>

                        <ion-button @click="enableIncomingMail"
                                    size="default"
                                    style="margin: 30px 0 0 15px;">
                            <ion-icon ios='lock-open'
                                      md="lock-open"
                                      slot="start"></ion-icon>

                            {{ $t('ncrypt.incoming_mail.user_start_using_incoming_mail_button') }}
                        </ion-button>
                    </ion-card-content>
                </ion-card>
            </div>
            <div v-else>
                <ion-card class="floating">
                    <ion-card-content>
                        <ion-label>
                            {{ $t('ncrypt.incoming_mail.your_company_has_not_configured_incoming_mail') }}
                        </ion-label>
                    </ion-card-content>
                </ion-card>
            </div>
        </ion-content>
    </ion-page>
</template>
<script>
    import FlashMessages from "../components/ui-elements/FlashMessages";
    import DefaultHeader from "../components/nav/DefaultHeader";
    import IncomingMailMessages from "@/components/messages/list/IncomingMailMessages";
    import {ApiError} from "@/errors/ApiError";
    import {ADD_FLASH_MESSAGE} from "@/const/mutation-types";
    import {Notifier} from "@/services/Notifier";

    export default {
        components: {IncomingMailMessages, DefaultHeader, FlashMessages},
        data() {
            return {
                busy: true,
                initialized: false,
                messages: this.$store.getters.incomingMail,
                unwatch: null,
            }
        },
        computed: {
            showSkeleton() {
                return this.busy && !this.initialized;
            },
            hasMessages() {
                const value = !!(this.messages && this.messages.length);

                if (value) {
                    this.$refs.content.classList.remove("bg-image");
                } else {
                    this.$refs.content.classList.add("bg-image");
                }

                return value;
            },
            incomingMailAddress() {
                if (
                    !this.$store.getters.authed
                    || !this.$store.getters.user.incoming_mail_address
                    || !this.$store.getters.user.friendly_incoming_mail_address
                ) {
                    return null;
                }

                return this.$store.getters.user.friendly_incoming_mail_address;
            },
        },
        methods: {
            async enableIncomingMail() {
                const loader = await this.$ionic.loadingController.create({
                    duration: 1000000,
                });

                await loader.present();

                const axiosConfig = {
                    validateStatus: status => status < 400,
                };

                this.$http.post('/incoming-mail/enable', {}, axiosConfig)
                    .then(async () => {
                        await this.$store.commit(ADD_FLASH_MESSAGE, {
                            message: this.$t('ncrypt.notifications.user_incoming_mail_is_now_enabled'),
                            type: 'success',
                        });

                        await this.$store.dispatch('refreshUser');
                    })
                    .catch(reason => new ApiError(reason))
                    .finally(() => loader.dismiss());
            },
            async startLoading() {
                this.busy = true;
                this.loader = await this.$ionic.loadingController.create({
                    message: this.$t('ncrypt.loader.refreshing'),
                    duration: 4000,
                });

                await this.loader.present();
            },
            finishLoading() {
                if (this.loader) {
                    this.loader.dismiss();
                    this.loader = null;
                }

                this.busy = false;
            },
            getCurrentPage() {
                return this.$refs.messages.getCurrentPage();
            },
            async refresh(event) {
                this.initialized = false;
                await this.startLoading();

                await this.$store.dispatch('refreshIncomingMail');

                if (typeof event.target.complete === 'function') {
                    event.target.complete();
                }

                this.finishLoading();
                this.initialized = true;
            },
            async loadData(event) {
                if (!this.getCurrentPage() || this.$refs.messages.scrollEndReached || this.busy) {
                    event.target.complete();
                    return;
                }

                this.busy = true;

                await this.$store.dispatch('getIncomingMailForPage', this.getCurrentPage() + 1);

                this.busy = false;
                event.target.complete();
            },
        },
        mounted() {
            this.unwatch = this.$store.watch(
                (state, getters) => getters.incomingMail,
                newValue => this.messages = newValue,
            );

            this.$nextTick(async () => {
                if (this.incomingMailAddress && !this.messages.length) {
                    await this.startLoading();

                    await this.$store.dispatch('getIncomingMailForPage', this.getCurrentPage());

                    this.finishLoading();
                }
            });

            this.initialized = true;
        },
        destroyed() {
            this.unwatch();
        },
    }
</script>
